/*
 * Display precision comes from the cashier-admin currency config
 * (`maximumFractionDigits`). These defaults only apply when admin has no value
 * for a currency, e.g. a conversion or provider currency that is not configured
 * for the site, or before the config has loaded.
 */
const FIAT_FRACTION_DIGITS = 2;
const CRYPTO_FRACTION_DIGITS = 4;

/*
 * Fiat codes the cashier deals with plus the stablecoins that display like
 * fiat. Kept static so they resolve to 2 digits before the config loads and on
 * engines without Intl.supportedValuesOf; any other ISO 4217 code is fiat too.
 */
const FIAT_LIKE_CODES = new Set([
  "USD",
  "EUR",
  "JPY",
  "BRL",
  "CNY",
  "THB",
  "TRY",
  "CAD",
  "AUD",
  "INR",
  "VND",
  "KRW",
  "USDT",
  "USDC",
  "DAI",
  "BUSD",
]);

type CurrencyType = "banking" | "crypto";

type FractionDigitsConfig = {
  type?: CurrencyType | string | null;
  fractionDigits?: number | null;
};

let isoCurrencyCodes: Set<string> | undefined;

const isIsoCurrencyCode = (currency: string) => {
  try {
    isoCurrencyCodes ??= new Set(Intl.supportedValuesOf("currency"));
    return isoCurrencyCodes.has(currency);
  } catch {
    return false;
  }
};

const isFiatLike = (currency: string) =>
  FIAT_LIKE_CODES.has(currency) || isIsoCurrencyCode(currency);

const getFractionDigits = (
  currency?: string,
  config?: FractionDigitsConfig,
) => {
  if (typeof config?.fractionDigits === "number") {
    return config.fractionDigits;
  }
  const isFiat = config?.type
    ? config.type === "banking"
    : isFiatLike(currency ?? "");
  return isFiat ? FIAT_FRACTION_DIGITS : CRYPTO_FRACTION_DIGITS;
};

export type { CurrencyType, FractionDigitsConfig };
export { getFractionDigits };
